/**
 * DashboardGestao — normalização do response da API (V_CENARIO_MENSAL)
 * para o modelo consumido pelos componentes do dashboard.
 *
 * A API devolve um array plano por filial/mês, com números em string e
 * campos nulos. Aqui esse formato é convertido em séries mensais ordenadas
 * por filial, mais a série agregada "geral".
 */
import DashProvider from '@/application/providers/DashProvider';
import { t } from 'i18next';

const MONTH_KEYS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const GERAL_KEY = 'geral';
const DEFAULT_PERIODO = 'past-6';

const PAST_PERIODOS = [
	{ value: 'past-3', direction: 'past', months: 3, label: 'Últimos 3 meses' },
	{ value: 'past-6', direction: 'past', months: 6, label: 'Últimos 6 meses' },
	{ value: 'past-12', direction: 'past', months: 12, label: 'Últimos 12 meses' },
];

const NEXT_PERIODOS = [
	{ value: 'next-3', direction: 'next', months: 3, label: 'Próximos 3 meses' },
	{ value: 'next-6', direction: 'next', months: 6, label: 'Próximos 6 meses' },
];

/** Campos cuja agregação é média ponderada pela quantidade de cenários. */
const WEIGHTED_FIELDS = [
	'scoreMedio',
	'uplift',
	'swapManual',
	'custoReal',
	'custoMeta',
	'custoPub',
	'leadTimeDias',
	'issuesCriticos',
	'issuesMedios',
	'issuesBaixos',
];

const COUNT_FIELDS = [
	'nCenarios',
	'publicados',
	'comSugestaoIA',
	'aprovadosSemCrit',
	'semReversao',
	'touchlessCount',
];

/** Casas decimais por métrica — alinhadas ao que o dashboard exibe. */
const DASH_PRECISION = {
	scoreMedio: 0,
	uplift: 1,
	swapManual: 4,
	swapManualPercent: 1,
	custoReal: 3,
	custoMeta: 3,
	custoPub: 3,
	custoVar: 1,
	leadTimeDias: 1,
	leadTime: 1,
	touchless: 0,
	touchlessRate: 4,
	issuesCriticos: 1,
	issuesMedios: 1,
	issuesBaixos: 1,
	conv: 0,
	width: 1,
};

function roundDashValue(value, field) {
	if (value === null || value === undefined) return null;
	if (!Number.isFinite(value)) return null;
	const decimals = DASH_PRECISION[field] ?? 2;
	const factor = 10 ** decimals;
	return Math.round(value * factor) / factor;
}

function roundMonthlyRow(row) {
	if (!row) return row;
	const rounded = { ...row };
	WEIGHTED_FIELDS.forEach(field => {
		rounded[field] = roundDashValue(row[field], field);
	});
	rounded.touchlessRate = roundDashValue(row.touchlessRate, 'touchlessRate');
	return rounded;
}

function toNumber(value) {
	if (value === null || value === undefined) return null;
	const raw = String(value).trim();
	if (!raw.length) return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
}

function toCount(value) {
	return toNumber(value) || 0;
}

function parseMesCard(value) {
	const match = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(String(value || '').trim());
	if (!match) return null;
	const [, , month, year] = match;
	const monthIndex = Number(month) - 1;
	if (monthIndex < 0 || monthIndex > 11) return null;
	return {
		mes: `${year}-${month}`,
		label: `${t(`month_short_${MONTH_KEYS[monthIndex]}`)}/${year.slice(2)}`,
	};
}

/** Mês âncora do calendário local (YYYY-MM). Não usa DTMESCARD da sessão. */
function getCurrentMesKey(date = new Date()) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	return `${year}-${month}`;
}

function addMonths(mesKey, delta) {
	const match = /^(\d{4})-(\d{2})$/.exec(String(mesKey || ''));
	if (!match) return mesKey;
	const year = Number(match[1]);
	const monthIndex = Number(match[2]) - 1;
	const shifted = new Date(year, monthIndex + delta, 1);
	return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
}

function hasFutureMonths(rows, currentMes) {
	return (rows || []).some(row => row && row.mes > currentMes);
}

/** Catálogo do select: passados sempre; próximos só se existir mês > atual em geral. */
function buildPeriodos(dadosMensais) {
	const geral = (dadosMensais && dadosMensais[GERAL_KEY]) || [];
	const periodos = PAST_PERIODOS.map(p => ({ ...p }));
	if (hasFutureMonths(geral, getCurrentMesKey())) {
		NEXT_PERIODOS.forEach(p => periodos.push({ ...p }));
	}
	return periodos;
}

function parsePeriodo(periodo) {
	if (periodo && typeof periodo === 'object') {
		return {
			value: periodo.value,
			direction: periodo.direction === 'next' ? 'next' : 'past',
			months: Number(periodo.months) || 6,
		};
	}
	const match = /^(past|next)-(\d+)$/.exec(String(periodo || ''));
	if (!match) {
		return { value: DEFAULT_PERIODO, direction: 'past', months: 6 };
	}
	return {
		value: String(periodo),
		direction: match[1],
		months: Number(match[2]),
	};
}

function mapRow(row) {
	const periodo = parseMesCard(row && row.DTMESCARD);
	if (!periodo) return null;
	const nCenarios = toCount(row.QTDE_CENARIOS);
	const touchlessCount = toCount(row.QTDE_PUB_SEM_ALTERACAO);
	const publicados = toCount(row.QTDE_PUBLICADOS);
	return roundMonthlyRow({
		label: periodo.label,
		mes: periodo.mes,
		nCenarios,
		publicados,
		comSugestaoIA: toCount(row.QTDE_COM_TROCAS_IA),
		aprovadosSemCrit: toCount(row.QTDE_PUB_SEM_CRITICO),
		semReversao: toCount(row.QTDE_SEM_IA_DESFEITA),
		touchlessCount,
		touchlessRate: nCenarios ? touchlessCount / nCenarios : 0,
		scoreMedio: toNumber(row.SCORE_MEDIO),
		uplift: toNumber(row.UPLIFT_MEDIO),
		swapManual: toNumber(row.PCT_ALTERACAO_MEDIO),
		custoReal: toNumber(row.VRPU_MEDIO),
		custoMeta: toNumber(row.VRMETA_MEDIO),
		custoPub: toNumber(row.VRPU_MEDIO_PUBLICADO),
		leadTimeDias: toNumber(row.LEADTIME_MEDIO_DIAS),
		issuesCriticos: toNumber(row.ISSUES_CRIT_MEDIO),
		issuesMedios: toNumber(row.ISSUES_MED_MEDIO),
		issuesBaixos: toNumber(row.ISSUES_BAIXA_MEDIO),
	});
}

function sumField(rows, field) {
	return rows.reduce((total, row) => total + (row[field] || 0), 0);
}

function weightedAverage(rows, field) {
	let weightSum = 0;
	let valueSum = 0;
	rows.forEach(row => {
		const value = row[field];
		if (value === null || value === undefined) return;
		const weight = row.nCenarios > 0 ? row.nCenarios : 1;
		valueSum += value * weight;
		weightSum += weight;
	});
	return weightSum ? valueSum / weightSum : null;
}

function mergeMonthRows(rows) {
	const merged = { label: rows[0].label, mes: rows[0].mes };
	COUNT_FIELDS.forEach(field => {
		merged[field] = sumField(rows, field);
	});
	WEIGHTED_FIELDS.forEach(field => {
		merged[field] = weightedAverage(rows, field);
	});
	merged.touchlessRate = merged.nCenarios ? merged.touchlessCount / merged.nCenarios : 0;
	return roundMonthlyRow(merged);
}

function groupByMonth(rows) {
	const byMonth = new Map();
	rows.forEach(row => {
		const bucket = byMonth.get(row.mes) || [];
		bucket.push(row);
		byMonth.set(row.mes, bucket);
	});
	return Array.from(byMonth.values())
		.map(mergeMonthRows)
		.sort((a, b) => a.mes.localeCompare(b.mes));
}

function buildDashboardModel(data, filiaisCatalog = []) {
	const rows = Array.isArray(data) ? data : [];
	const byFilial = new Map();
	const allRows = [];
	const filialNomeById = new Map();

	filiaisCatalog.forEach(filial => {
		const id = String(filial.CDFILIAL).trim();
		if (!id) return;
		filialNomeById.set(id, String(filial.NMFILIAL).trim());
	});

	rows.forEach(rawRow => {
		const filialId = String((rawRow && rawRow.CDFILIAL) || '').trim();
		const row = filialId ? mapRow(rawRow) : null;
		if (!row) return;
		const bucket = byFilial.get(filialId) || [];
		bucket.push(row);
		byFilial.set(filialId, bucket);
		allRows.push(row);
	});

	 const filiais = Array.from(byFilial.keys())
		.sort((a, b) => a.localeCompare(b))
		.map(id => ({
			id,
			nome: filialNomeById.get(id) || id, // fallback
		}));

	const dadosMensais = filiais.reduce((acc, filial) => {
		acc[filial.id] = groupByMonth(byFilial.get(filial.id));
		return acc;
	}, {});
	dadosMensais[GERAL_KEY] = groupByMonth(allRows);

	return { filiais, periodos: buildPeriodos(dadosMensais), dadosMensais };
}

function getDashboardModel() {
  // LUGAR DE MOCK!
	return Promise.all([
		DashProvider.getDashData(),
		DashProvider.getFiliais().catch(() => []),
	]).then(([data, filiaisCatalog]) => buildDashboardModel(data, filiaisCatalog));
	// return DashProvider.getDashData().then(data => buildDashboardModel(mockData));
}


export default {
	GERAL_KEY,
	DEFAULT_PERIODO,
	DASH_PRECISION,
	roundDashValue,
	getCurrentMesKey,
	addMonths,
	buildPeriodos,
	parsePeriodo,
	buildDashboardModel,
	getDashboardModel,
};
